/**
 * Local Markets - Nigerian market price records, trends and comparisons.
 * Rendered by the Markets shell (one section per shell tab via the `view`
 * prop); the legacy /app/local-markets route redirects into the shell.
 * Supports per-bag and per-kg views (FR-200).
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { PageHeader, ChartCard, LoadingSkeleton, EmptyState, StatCard, FilterSelect, UpdatedStamp, getChartStyles, ExportButton, DepthNotice } from '../../components/common';
import { useMarkets, useCommodities, useCategories, useLocalPrices, useStates } from '../../services/queries';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useRecipes } from '../../theme/recipes';
import { fmtDate } from '../../utils/format';
import { getPrefs } from '../../utils/preferences';
import { activeCategories, isCommodityActive } from '../../utils/catalogStatus';

// Shared monospace font for numeric values
const firaCode = { fontFamily: MONO };


const HEAD_CELLS = [
  { id: 'date', label: 'Date' },
  { id: 'marketName', label: 'Market' },
  { id: 'stateName', label: 'State' },
  { id: 'commodityName', label: 'Commodity' },
  { id: 'bagSize', label: 'Bag Size', align: 'right' },
  { id: 'price', label: 'Price (per bag)', align: 'right' },
  { id: 'pricePerKg', label: 'Price (per kg)', align: 'right' }
];

function LocalMarkets({ embedded = false, view }) {
  const { isDark, palette } = useThemeMode();
  const chartStyles = getChartStyles(isDark);
  const recipes = useRecipes();
  const { user: authUser } = useAuth();
  // Which section to render; the Markets shell owns the tab strip.
  const section = view || 'records';

  // Filter states (State/Market/Category are multi-select)
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  // Initial unit comes from the user's Settings preference.
  const [priceUnit, setPriceUnit] = useState(() => getPrefs(authUser?.email).priceUnit);
  const [searchTerm, setSearchTerm] = useState('');

  // Table sort + pagination
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data: markets = [], isLoading: l1 } = useMarkets();
  const { data: commodities = [], isLoading: l2 } = useCommodities();
  const { data: commodityCategories = [], isLoading: l3 } = useCategories();
  const { data: localPrices = [], isLoading: l4 } = useLocalPrices();
  const { data: pilotStates = [], isLoading: l5 } = useStates();
  const loading = l1 || l2 || l3 || l4 || l5;

  const marketById = useMemo(() => new Map(markets.map(m => [m.id, m])), [markets]);
  const commodityById = useMemo(() => new Map(commodities.map(c => [c.id, c])), [commodities]);

  const matchesStates = (market, stateValues) =>
    !!market && stateValues.some(sv => market.state.toLowerCase().includes(sv.toLowerCase()));

  // Markets offered in the Market dropdown, narrowed by the selected states
  const filteredMarkets = useMemo(() => {
    if (selectedStates.length === 0) return markets;
    return markets.filter(m => matchesStates(m, selectedStates));
  }, [selectedStates, markets]);

  // Filter price records (category now genuinely applies via commodity lookup)
  const filteredPrices = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    return localPrices.filter(price => {
      const market = marketById.get(price.marketId);
      const commodity = commodityById.get(price.commodityId);

      if (selectedStates.length > 0 && !matchesStates(market, selectedStates)) return false;
      if (selectedMarkets.length > 0 && !selectedMarkets.includes(price.marketId)) return false;
      if (selectedCategories.length > 0 && !selectedCategories.includes(commodity?.category)) return false;

      if (searchLower) {
        return (
          commodity?.name.toLowerCase().includes(searchLower) ||
          market?.name.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }, [selectedStates, selectedMarkets, selectedCategories, searchTerm, marketById, commodityById, localPrices]);

  // Back to page 1 whenever the row set changes
  useEffect(() => {
    setPage(0);
  }, [selectedStates, selectedMarkets, selectedCategories, searchTerm]);

  // Group prices by commodity for chart
  const chartData = useMemo(() => {
    const grouped = {};
    filteredPrices.forEach(price => {
      const priceValue = priceUnit === 'kg' ? price.pricePerKg : price.price;
      // A row with no figure is not an observation of ₦0. It used to be added
      // as 0 while still incrementing the count, which pulled the monthly
      // average down by however many rows were blank.
      if (priceValue == null) return;
      const dateKey = price.date.slice(0, 7); // YYYY-MM
      if (!grouped[dateKey]) {
        grouped[dateKey] = { date: dateKey, totalPrice: 0, count: 0 };
      }
      grouped[dateKey].totalPrice += priceValue;
      grouped[dateKey].count += 1;
    });

    return Object.values(grouped)
      .map(item => ({
        date: item.date,
        avgPrice: Math.round(item.totalPrice / item.count)
      }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-12);
  }, [filteredPrices, priceUnit]);

  // Price statistics
  const priceStats = useMemo(() => {
    if (filteredPrices.length === 0) return null;

    // Nulls filtered before the reduce and the Math.min/max: a null adds 0 to
    // the sum while counting, and Math.min(null, 900) is 0, so a single blank
    // row reported a minimum price of ₦0.
    const prices = filteredPrices
      .map(p => priceUnit === 'kg' ? p.pricePerKg : p.price)
      .filter(v => v != null);
    if (prices.length === 0) return null;
    return {
      avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
      min: Math.min(...prices),
      max: Math.max(...prices),
      count: prices.length
    };
  }, [filteredPrices, priceUnit]);

  // Latest record date across currently filtered prices
  const latestFilteredDate = useMemo(() => {
    let max = null;
    for (const p of filteredPrices) {
      if (!max || p.date > max) max = p.date;
    }
    return max;
  }, [filteredPrices]);

  const unitSuffix = priceUnit === 'kg' ? 'per kg' : 'per bag';

  // Rows enriched with display names so sorting and export share one shape
  const tableRows = useMemo(() => filteredPrices.map(p => {
    const market = marketById.get(p.marketId);
    const commodity = commodityById.get(p.commodityId);
    return {
      ...p,
      marketName: market?.name || 'Unknown',
      stateName: market?.state || '',
      commodityName: commodity?.name || 'Unknown',
      // History keeps retired commodities visible, tagged so the archive
      // is honest about what is no longer tracked.
      retired: commodity ? !isCommodityActive(commodity, commodityCategories) : false
    };
  }), [filteredPrices, marketById, commodityById, commodityCategories]);

  const sortedRows = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1;
    const rows = [...tableRows];
    rows.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [tableRows, order, orderBy]);

  const pagedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(columnId);
      setOrder(columnId === 'date' ? 'desc' : 'asc');
    }
  };

  // Market comparison data
  const marketComparisonData = useMemo(() => {
    const byMarket = {};
    filteredPrices.forEach(price => {
      const market = marketById.get(price.marketId);
      if (!market) return;

      const value = priceUnit === 'kg' ? price.pricePerKg : price.price;
      if (value == null) return;  // same reason as the monthly average above

      if (!byMarket[market.id]) {
        byMarket[market.id] = {
          marketId: market.id,
          name: market.name,
          state: market.state,
          totalPrice: 0,
          count: 0
        };
      }

      byMarket[market.id].totalPrice += value;
      byMarket[market.id].count += 1;
    });

    return Object.values(byMarket)
      .map(m => ({
        ...m,
        avgPrice: Math.round(m.totalPrice / m.count)
      }))
      .sort((a, b) => b.avgPrice - a.avgPrice)
      .slice(0, 10);
  }, [filteredPrices, priceUnit, marketById]);

  // Deselecting a state also drops markets that fall out of scope
  const handleStatesChange = (next) => {
    setSelectedStates(next);
    if (next.length > 0) {
      setSelectedMarkets(prev => prev.filter(id => matchesStates(marketById.get(id), next)));
    }
  };

  const clearFilters = () => {
    setSelectedStates([]);
    setSelectedMarkets([]);
    setSelectedCategories([]);
    setSearchTerm('');
  };

  const hasActiveFilters =
    selectedStates.length > 0 || selectedMarkets.length > 0 || selectedCategories.length > 0 || searchTerm.trim() !== '';

  const formatPrice = (value) => {
    return `₦${value.toLocaleString()}${priceUnit === 'kg' ? '/kg' : ''}`;
  };

  // Exports EVERY filtered row (the table paginates, the file does not).
  // Shared so both buttons send exactly the same file.
  const exportHeaders = ['Date', 'Market', 'State', 'Commodity', 'Bag size (kg)', 'Price per bag (NGN)', 'Price per kg (NGN)'];
  const exportRows = sortedRows.map((r) => [r.date, r.marketName, r.stateName, r.commodityName, r.bagSize, r.price, r.pricePerKg]);

  if (loading) {
    return embedded ? (
      <LoadingSkeleton variant="page" />
    ) : (
      <Box>
        <PageHeader
          title="Local Markets"
          subtitle="Track commodity prices across Nigerian markets"
          breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Local Markets' }]}
        />
        <LoadingSkeleton variant="page" />
      </Box>
    );
  }

  return (
    <Box>
      <DepthNotice />
      {!embedded && (
        <PageHeader
          eyebrow="Analytics"
          title="Local Markets"
          subtitle="Track commodity prices across Nigerian markets"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Local Markets' }
          ]}
          actions={
            <ExportButton
              filename="local-market-prices"
              label="Export CSV"
              headers={exportHeaders}
              rows={exportRows}
            />
          }
        />
      )}

      {/* Filter strip */}
      <Box sx={{ ...recipes.stickyStripSx, mb: 3 }}>
        <Grid container spacing={1.5} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={3.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search commodities or markets"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={recipes.searchSx}
              inputProps={{ 'aria-label': 'Search commodities or markets' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="ph:magnifying-glass" width={17} height={17} color={palette.textMuted} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {/* State Filter */}
          <Grid item xs={6} sm={4} md={2}>
            <FilterSelect
              label="State"
              multiple
              plural="states"
              value={selectedStates}
              onChange={handleStatesChange}
              options={pilotStates.map(state => ({ value: state.name.split(' ')[0], label: state.name }))}
            />
          </Grid>

          {/* Market Filter */}
          <Grid item xs={6} sm={4} md={2}>
            <FilterSelect
              label="Market"
              multiple
              plural="markets"
              value={selectedMarkets}
              onChange={setSelectedMarkets}
              options={filteredMarkets.map(market => ({ value: market.id, label: market.name, secondary: market.state }))}
            />
          </Grid>

          {/* Category Filter */}
          <Grid item xs={6} sm={4} md={2}>
            <FilterSelect
              label="Category"
              multiple
              plural="categories"
              value={selectedCategories}
              onChange={setSelectedCategories}
              options={activeCategories(commodityCategories).map(cat => ({ value: cat.id, label: cat.name }))}
            />
          </Grid>

          {/* Unit Toggle */}
          <Grid item xs={6} sm={4} md={2.5}>
            <ToggleButtonGroup
              value={priceUnit}
              exclusive
              onChange={(e, val) => val && setPriceUnit(val)}
              size="small"
              fullWidth
              aria-label="Price unit"
              sx={recipes.toggleGroupSx}
            >
              <ToggleButton value="bag">Per Bag</ToggleButton>
              <ToggleButton value="kg">Per KG</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>

        {/* Active Filters */}
        {hasActiveFilters && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: palette.textMuted }}>
              Active filters:
            </Typography>
            {searchTerm.trim() !== '' && (
              <Chip
                size="small"
                sx={recipes.filterChipSx}
                label={`Search: "${searchTerm.trim()}"`}
                onDelete={() => setSearchTerm('')}
              />
            )}
            {selectedStates.map(sv => (
              <Chip
                key={sv}
                size="small"
                sx={recipes.filterChipSx}
                label={`State: ${pilotStates.find(s => s.name.split(' ')[0] === sv)?.name || sv}`}
                onDelete={() => handleStatesChange(selectedStates.filter(v => v !== sv))}
              />
            ))}
            {selectedMarkets.map(id => (
              <Chip
                key={id}
                size="small"
                sx={recipes.filterChipSx}
                label={`Market: ${marketById.get(id)?.name || id}`}
                onDelete={() => setSelectedMarkets(selectedMarkets.filter(v => v !== id))}
              />
            ))}
            {selectedCategories.map(id => (
              <Chip
                key={id}
                size="small"
                sx={recipes.filterChipSx}
                label={`Category: ${commodityCategories.find(c => c.id === id)?.name || id}`}
                onDelete={() => setSelectedCategories(selectedCategories.filter(v => v !== id))}
              />
            ))}
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ fontSize: '0.75rem', fontWeight: 700, color: palette.accent, '&:hover': { bgcolor: palette.accentBg } }}
            >
              Clear all
            </Button>
          </Box>
        )}
      </Box>

      {/* Stats Summary */}
      {priceStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="Records"
              value={priceStats.count.toLocaleString()}
              tone="neutral"
              icon={<Icon icon="ph:rows" width={20} height={20} />}
              sub={<UpdatedStamp date={latestFilteredDate} />}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label={`Average Price (${unitSuffix})`}
              value={formatPrice(priceStats.avg)}
              tone="accent"
              icon={<Icon icon="ph:chart-line" width={20} height={20} />}
              sub="across current filters"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label={`Lowest Price (${unitSuffix})`}
              value={formatPrice(priceStats.min)}
              tone="info"
              icon={<Icon icon="ph:arrow-down-right" width={20} height={20} />}
              sub="cheapest record in range"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label={`Highest Price (${unitSuffix})`}
              value={formatPrice(priceStats.max)}
              tone="info"
              icon={<Icon icon="ph:arrow-up-right" width={20} height={20} />}
              sub="dearest record in range"
            />
          </Grid>
        </Grid>
      )}

      {/* Section content (tab strip lives in the Markets shell) */}
      {section === 'records' && (
        <Card>
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              borderBottom: `1px solid ${palette.divider}`
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Price records</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: palette.textMuted }}>
                {sortedRows.length.toLocaleString()} record{sortedRows.length === 1 ? '' : 's'} match the current filters
              </Typography>
            </Box>
            <ExportButton
              filename="local-market-prices"
              label="Export CSV"
              headers={exportHeaders}
              rows={exportRows}
              sx={{
                borderColor: palette.cardBorder,
                color: palette.text,
                fontWeight: 600,
                '&:hover': { borderColor: palette.accent, bgcolor: palette.accentBg },
              }}
            />
          </Box>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {sortedRows.length === 0 ? (
              <EmptyState
                compact
                variant="dashed"
                title="No matching records"
                description="No price records match the current filters. Try adjusting or clearing them."
                actionLabel={hasActiveFilters ? 'Clear filters' : undefined}
                onAction={hasActiveFilters ? clearFilters : undefined}
                sx={{ m: 2.5 }}
              />
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {HEAD_CELLS.map(cell => (
                          <TableCell key={cell.id} align={cell.align || 'left'} sx={recipes.headCellSx}>
                            <TableSortLabel
                              active={orderBy === cell.id}
                              direction={orderBy === cell.id ? order : 'asc'}
                              onClick={() => handleSort(cell.id)}
                              sx={recipes.sortLabelSx}
                            >
                              {cell.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedRows.map((row) => (
                        <TableRow key={row.id} hover sx={recipes.bodyRowSx}>
                          <TableCell sx={{ ...firaCode, color: palette.textMuted, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {fmtDate(row.date)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {row.marketName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                              {row.stateName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2">{row.commodityName}</Typography>
                              {row.retired && <Chip label="RETIRED" size="small" sx={{ ml: 0.75, height: 16, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.06em', bgcolor: palette.surfaceHover, color: palette.textMuted }} />}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={firaCode}>{row.bagSize}kg</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={priceUnit === 'bag' ? 600 : 400} sx={firaCode}>
                              {row.price != null ? `₦${row.price.toLocaleString()}` : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={priceUnit === 'kg' ? 600 : 400} sx={firaCode}>
                              {row.pricePerKg != null ? `₦${row.pricePerKg.toLocaleString()}` : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ ...recipes.tableFooterSx, justifyContent: 'flex-end', py: 0 }}>
                  <TablePagination
                    component="div"
                    count={sortedRows.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    sx={recipes.paginationSx}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {section === 'trends' && (
        <ChartCard
          title="Average Price Trend"
          subtitle="Monthly average prices based on current filters · Source: Field Agents"
          stamp={latestFilteredDate}
          height={400}
        >
          {chartData.length === 0 ? (
            <EmptyState
              compact
              variant="dashed"
              title="No trend data"
              description="No price records match the current filters, so there is nothing to chart."
              actionLabel={hasActiveFilters ? 'Clear filters' : undefined}
              onAction={hasActiveFilters ? clearFilters : undefined}
            />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid {...chartStyles.grid} />
                <XAxis dataKey="date" {...chartStyles.axis} />
                <YAxis
                  {...chartStyles.axis}
                  tickFormatter={(v) => `₦${(v/1000).toFixed(0)}k`}
                />
                <Tooltip
                  {...chartStyles.tooltip}
                  formatter={(value) => [formatPrice(value), 'Avg Price']}
                />
                <Line
                  type="monotone"
                  dataKey="avgPrice"
                  stroke={palette.navy}
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      )}

      {section === 'comparison' && (
        <ChartCard
          title="Market Price Comparison"
          subtitle="Top 10 markets by average price · Source: Field Agents"
          stamp={latestFilteredDate}
          height={400}
        >
          {marketComparisonData.length === 0 ? (
            <EmptyState
              compact
              variant="dashed"
              title="No comparison data"
              description="No price records match the current filters, so there are no markets to compare."
              actionLabel={hasActiveFilters ? 'Clear filters' : undefined}
              onAction={hasActiveFilters ? clearFilters : undefined}
            />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={marketComparisonData} layout="vertical">
                <CartesianGrid {...chartStyles.grid} />
                <XAxis
                  type="number"
                  {...chartStyles.axis}
                  tickFormatter={(v) => `₦${(v/1000).toFixed(0)}k`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  {...chartStyles.axis}
                  tick={{ ...chartStyles.axis.tick, fontSize: 11 }}
                  width={100}
                />
                <Tooltip
                  {...chartStyles.tooltip}
                  formatter={(value) => [formatPrice(value), 'Avg Price']}
                />
                <Bar dataKey="avgPrice" fill={palette.navy} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      )}
    </Box>
  );
}

export default LocalMarkets;
